// useImport.ts (S26) - the import + persistence bridge for the ImportReview surface.
//   POST /api/import  { text, format } -> { handRecord }         (S16, pure compute)
//   POST /api/hands   { sessionId, handRecord } -> { id }        (S14 persistence)
// Both routes are Access-gated in the Worker; Cloudflare Access injects the identity header in prod,
// and the vite dev proxy injects a dev identity locally, so the web client sends no auth header. A
// 400 from /api/import is the parser's specific reason (bad paste) and is surfaced verbatim; anything
// else is a generic failure.
import { useState } from 'react';
import { ImportResponseSchema, type HandRecord, type ImportRequest } from '@postflop/schema';

export interface ImportState {
  loading: boolean;
  record: HandRecord | null;
  /** the parser's exact reason on a 400, or a generic message on any other failure. */
  error: string | null;
  /** the persisted hand id after a successful save, if the user saved it. */
  savedId: string | null;
  saving: boolean;
}

const EMPTY: ImportState = { loading: false, record: null, error: null, savedId: null, saving: false };

export function useImport() {
  const [state, setState] = useState<ImportState>(EMPTY);

  const run = async (text: string, format: ImportRequest['format'] = 'auto') => {
    setState({ ...EMPTY, loading: true });
    try {
      const res = await fetch('/api/import', {
        method: 'POST',
        headers: { 'content-type': 'application/json', accept: 'application/json' },
        body: JSON.stringify({ text, format } satisfies ImportRequest),
      });
      const body = (await res.json()) as { handRecord?: unknown; error?: string };
      if (!res.ok) {
        setState({ ...EMPTY, error: body.error ?? `import failed (${res.status})` });
        return;
      }
      const parsed = ImportResponseSchema.parse(body);
      setState({ ...EMPTY, record: parsed.handRecord });
    } catch {
      setState({ ...EMPTY, error: 'could not reach the import service' });
    }
  };

  // Persist an imported hand: open a review session (hands.session_id is a required FK), then persist
  // the HandRecord against it. `startedAt` is stamped client-side (the browser clock is fine here).
  const save = async (record: HandRecord, startedAt: number) => {
    setState((s) => ({ ...s, saving: true }));
    try {
      const sb = record.spec.stakes;
      const sessionRes = await fetch('/api/sessions', {
        method: 'POST',
        headers: { 'content-type': 'application/json', accept: 'application/json' },
        body: JSON.stringify({
          mode: 'no_tips',
          gameType: record.spec.gameType,
          stakesLabel: `$${sb.sb}/$${sb.bb}`,
          startedAt,
        }),
      });
      const sessionBody = (await sessionRes.json()) as { id?: string; error?: string };
      if (!sessionRes.ok || !sessionBody.id) {
        setState((s) => ({ ...s, saving: false, error: sessionBody.error ?? `save failed (${sessionRes.status})` }));
        return;
      }
      const res = await fetch('/api/hands', {
        method: 'POST',
        headers: { 'content-type': 'application/json', accept: 'application/json' },
        body: JSON.stringify({ sessionId: sessionBody.id, handRecord: record }),
      });
      const body = (await res.json()) as { id?: string; error?: string };
      if (!res.ok || !body.id) {
        setState((s) => ({ ...s, saving: false, error: body.error ?? `save failed (${res.status})` }));
        return;
      }
      setState((s) => ({ ...s, saving: false, savedId: body.id ?? null }));
    } catch {
      setState((s) => ({ ...s, saving: false, error: 'could not reach the save service' }));
    }
  };

  const reset = () => setState(EMPTY);

  return { state, run, save, reset };
}
